import React, { useMemo } from 'react';
import { extend } from '@thd-nucleus/data-sources';
import {
  ProductPod,
  ProductImage,
  ProductIdentifier,
  ProductRating,
  PodTapper,
  PodSpacer,
  PodRow,
  ProductBadge,
  ProductSponsored
} from '@thd-olt-component-react/product-pod';
import PropTypes from 'prop-types';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Price } from '@thd-olt-component-react/price';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import {
  useRecsCarouselPodTelemetry,
  useNodeRef
} from '../hooks';
import { newRelicConstants } from '../core/constants';

export const DiscoveryZonesPod = (props) => {
  const {
    // eslint-disable-next-line react/prop-types
    itemId,
    storeId,
    position,
    parent,
    telemetryKey,
    scheme,
    anchorProduct,
    brandTitleMaxLine,
    zoneName,
    zonePosition,
    zoneSponsored,
    podResults,
    hideRatings,
    url,
    product
  } = props;

  // nodes
  const [productPodNodeRef, productPodNode] = useNodeRef();

  // -----------------
  // Telemetry
  // -----------------
  const {
    isSponsored,
    handleClick,
    handleClickOnProductImage,
    handleClickOnProductHeader,
    handleClickOnProductRating
  } = useRecsCarouselPodTelemetry({
    productPodNode,
    parent,
    title: telemetryKey,
    itemId,
    storeId,
    product,
    position,
    url,
    podKpiName: newRelicConstants.SPONSORED_DISCOVERY_ZONE_CAROUSEL
  });

  // --
  const showSponsoredLabel = useMemo(() => {
    return !zoneSponsored && isSponsored;
  }, [zoneSponsored, isSponsored]);

  return (
    <div
      ref={productPodNodeRef}
      data-product-id={itemId}
      data-testid="dz-product-pod"
    >
      <ProductPod
        itemId={itemId}
        storeId={storeId}
        analyticsData={{
          parent, position, scheme, anchorProduct, zoneName, zonePosition, podResults
        }}
        padding="sui-p-2"
        render={(pod) => (
          <>
            <a
              className="sui-top-0 sui-left-0 sui-absolute sui-size-full sui-z-1"
              href={url}
              aria-label="Link"
              onClick={handleClick}
            />
            <PodSpacer minHeight="40px">
              <PodRow noPadding>
                <ProductBadge itemId={pod.itemId} storeId={storeId} />
              </PodRow>
            </PodSpacer>
            <PodTapper zIndex>
              <ProductImage
                itemId={pod.itemId}
                showSecondaryImage={pod.showSecondaryImage}
                onClick={handleClickOnProductImage}
              />
            </PodTapper>

            <PodTapper fitWidth zIndex>
              <ProductHeader
                brand="above"
                itemId={pod.itemId}
                brandTitleMaxLine={brandTitleMaxLine}
                disableShopThisCollection
                productNameLineHeight="tight"
                onClick={handleClickOnProductHeader}
              />
            </PodTapper>

            {!hideRatings
              && (
                <PodTapper fitWidth zIndex>
                  <ProductRating
                    itemId={pod.itemId}
                    onClick={handleClickOnProductRating}
                  />
                </PodTapper>
              )}
            <PodTapper zIndex cursorPointer aPointer buttonPointer spanPointer handleClick={handleClick}>
              <Price
                itemId={pod.itemId}
                large={false}
                storeId={storeId}
                displayEachUom={false}
                showPreferredPricingBadge
              />
            </PodTapper>
            {showSponsoredLabel
              && (
                <div className="sui-flex sui-flex-col sui-h-full sui-justify-end">
                  <PodTapper zIndex handleClick={handleClick}>
                    <PodSpacer minHeight="30px">
                      <div className="sui-recs-sponsoredbadge">
                        <ProductSponsored itemId={pod.itemId} position={position} noBackground noPadding />
                      </div>
                    </PodSpacer>
                  </PodTapper>
                </div>
            )}
          </>
        )}
      />
    </div>
  );
};

DiscoveryZonesPod.dataModel = extend(
  ProductPod,
  ProductImage,
  ProductIdentifier,
  ProductHeader,
  SuperSkuMiniSwatches,
  Price,
  ProductRating
);

DiscoveryZonesPod.propTypes = {
  itemId: PropTypes.string.isRequired,
  storeId: PropTypes.string,
  position: PropTypes.number,
  parent: PropTypes.string.isRequired,
  telemetryKey: PropTypes.string.isRequired,
  scheme: PropTypes.string,
  anchorProduct: PropTypes.string,
  brandTitleMaxLine: PropTypes.number,
  zoneName: PropTypes.string,
  zonePosition: PropTypes.string,
  zoneSponsored: PropTypes.bool,
  podResults: PropTypes.number,
  hideRatings: PropTypes.bool,
  url: PropTypes.string.isRequired,
  product: PropTypes.shape({
    itemId: PropTypes.string.isRequired,
    identifiers: PropTypes.shape({
      canonicalUrl: PropTypes.string
    }),
    info: PropTypes.shape({
      isSponsored: PropTypes.bool,
      sponsoredBeacon: PropTypes.shape({
        onClickBeacons: PropTypes.arrayOf(PropTypes.string),
        onViewBeacons: PropTypes.arrayOf(PropTypes.string)
      }),
      sponsoredMetadata: PropTypes.shape({
        trackSource: PropTypes.string,
        campaignId: PropTypes.string,
        placementId: PropTypes.string,
        slotId: PropTypes.string,
        sponsoredId: PropTypes.string
      })
    })
  })
};

DiscoveryZonesPod.defaultProps = {
  storeId: '',
  position: '',
  scheme: 'drecs',
  anchorProduct: '',
  brandTitleMaxLine: 4,
  zoneName: '',
  zonePosition: '',
  zoneSponsored: false,
  podResults: 0,
  hideRatings: false
};
DiscoveryZonesPod.displayName = 'DiscoveryZonesPod';
